import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  ArrowLeft, Activity, Cpu, Server, AlertTriangle, Loader2, KeyRound, Boxes,
  HeartPulse, RefreshCw, Mail, CheckCircle2, XCircle, MinusCircle,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const nf = (n) => (Number(n) || 0).toLocaleString('de-CH');
const usd = (n) => '$' + (Number(n) || 0).toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const RANGES = [{ d: 1, l: 'Heute' }, { d: 7, l: '7 Tage' }, { d: 30, l: '30 Tage' }, { d: 90, l: '90 Tage' }];

const HEALTH_ICON = {
  up: { Icon: CheckCircle2, c: 'text-green-600', t: 'Funktioniert' },
  down: { Icon: XCircle, c: 'text-red-600', t: 'Ausgefallen' },
  not_configured: { Icon: MinusCircle, c: 'text-gray-400', t: 'Nicht konfiguriert' },
};

const HealthCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/integration-health/status`, { headers: headers() });
      setData(r.data);
    } catch (e) { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const runCheck = async (sendTest) => {
    setRunning(true);
    try {
      const r = await axios.post(
        `${API}/admin/integration-health/check?send_test=${sendTest ? 'true' : 'false'}`,
        {}, { headers: headers() },
      );
      const res = r.data?.results || {};
      const down = Object.values(res).filter((x) => x.status === 'down').length;
      if (sendTest) {
        toast.success(`Prüfung abgeschlossen · Test-Mail an ${data?.recipient || 'info@strongfit.ch'} gesendet` + (down ? ` · ${down} Ausfall` : ''));
      } else {
        toast.success(down ? `Prüfung abgeschlossen · ${down} Dienst(e) ausgefallen` : 'Prüfung abgeschlossen · alle Dienste funktionieren');
      }
      await loadStatus();
    } catch (e) {
      toast.error('Prüfung fehlgeschlagen: ' + (e?.response?.data?.detail || e.message));
    } finally { setRunning(false); }
  };

  const states = data?.states || [];

  return (
    <Card data-testid="integration-health-card">
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <HeartPulse className="w-5 h-5 text-rose-500" /> Schnittstellen-Status (Ausfall-Monitoring)
          </CardTitle>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => runCheck(false)} disabled={running} data-testid="health-check-btn">
              {running ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />} Jetzt prüfen
            </Button>
            <Button size="sm" onClick={() => runCheck(true)} disabled={running} data-testid="health-test-mail-btn">
              <Mail className="w-4 h-4 mr-1" /> Prüfen &amp; Test-Mail
            </Button>
          </div>
        </div>
        <p className="text-xs text-gray-500">
          Stündliche automatische Prüfung. Bei Ausfall (nach {data?.retry_attempts || 3} Versuchen) und bei
          Wiederherstellung geht sofort eine E-Mail an <strong>{data?.recipient || 'info@strongfit.ch'}</strong>.
          Automatischer Mail-Versand läuft nur im Live-System.
        </p>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="py-6 flex items-center justify-center gap-2 text-gray-500"><Loader2 className="w-4 h-4 animate-spin" /> Wird geladen…</div>
        ) : states.length === 0 ? (
          <p className="text-sm text-gray-500 py-4">Noch keine Prüfung gelaufen. Klicke auf „Jetzt prüfen".</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {states.map((s) => {
              const m = HEALTH_ICON[s.status] || HEALTH_ICON.not_configured;
              return (
                <div key={s.service} className="flex items-start gap-2 rounded-lg border border-gray-100 p-3" data-testid={`health-${s.service}`}>
                  <m.Icon className={`w-5 h-5 mt-0.5 shrink-0 ${m.c}`} />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-800">{s.label}</p>
                    <p className={`text-xs ${m.c}`}>{m.t}</p>
                    {s.detail && <p className="text-[11px] text-gray-400 truncate">{s.detail}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const KeyBadge = ({ type }) => {
  const map = {
    own: { t: 'Eigener Key', c: 'bg-green-100 text-green-700' },
    emergent: { t: 'Emergent-Key', c: 'bg-red-100 text-red-700' },
    none: { t: 'Kein Key', c: 'bg-gray-100 text-gray-500' },
  };
  const m = map[type] || map.none;
  return <span className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-medium ${m.c}`}>{m.t}</span>;
};

const AIUsagePage = () => {
  const navigate = useNavigate();
  const [days, setDays] = useState(30);
  const [sum, setSum] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([
        axios.get(`${API}/admin/ai-usage/summary?days=${days}`, { headers: headers() }),
        axios.get(`${API}/admin/ai-usage/config-map`, { headers: headers() }),
      ]);
      setSum(s.data);
      setCfg(c.data);
    } catch (e) {
      setSum(null);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const emergentCalls = (sum?.by_key_type || []).find((x) => x.key === 'emergent')?.calls || 0;

  return (
    <div className="space-y-6" data-testid="ai-usage-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} data-testid="ai-usage-back">
            <ArrowLeft className="w-4 h-4 mr-1" /> Dashboard
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">KI-Verbrauch &amp; API-Zuordnung</h1>
            <p className="text-sm text-gray-500">Wofür wird die API verbraucht — pro App-Teil, Modell und Key</p>
          </div>
        </div>
        <div className="flex gap-1" data-testid="ai-usage-range">
          {RANGES.map((r) => (
            <Button key={r.d} size="sm" variant={days === r.d ? 'default' : 'outline'}
              onClick={() => setDays(r.d)} data-testid={`range-${r.d}`}>{r.l}</Button>
          ))}
        </div>
      </div>

      {loading ? (
        <Card><CardContent className="py-12 flex items-center justify-center gap-2 text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Wird geladen…
        </CardContent></Card>
      ) : (
        <>
          {emergentCalls > 0 && (
            <div className="flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-800" data-testid="emergent-warning">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span><strong>{nf(emergentCalls)} Aufrufe</strong> liefen im gewählten Zeitraum noch über den <strong>Emergent-Key</strong> statt über euren eigenen API-Key.</span>
            </div>
          )}

          <HealthCard />

          {/* KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: Activity, l: 'KI-Aufrufe', v: nf(sum?.totals?.calls), c: 'text-sky-600', b: 'bg-sky-50', tid: 'kpi-calls' },
              { icon: Cpu, l: 'Geschätzte Tokens', v: nf(sum?.totals?.tokens), c: 'text-indigo-600', b: 'bg-indigo-50', tid: 'kpi-tokens' },
              { icon: Boxes, l: 'Geschätzte Kosten', v: usd(sum?.totals?.cost), c: 'text-emerald-600', b: 'bg-emerald-50', tid: 'kpi-cost' },
              { icon: AlertTriangle, l: 'Fehler', v: nf(sum?.totals?.errors), c: 'text-orange-600', b: 'bg-orange-50', tid: 'kpi-errors' },
            ].map((k) => (
              <Card key={k.l} data-testid={k.tid}><CardContent className="p-4 flex items-start justify-between">
                <div><p className="text-xs text-gray-500">{k.l}</p><p className={`text-2xl font-bold mt-1 ${k.c}`}>{k.v}</p></div>
                <div className={`p-2 rounded-lg ${k.b}`}><k.icon className={`w-5 h-5 ${k.c}`} /></div>
              </CardContent></Card>
            ))}
          </div>

          {/* Nach App-Teil */}
          <Card data-testid="by-source-card">
            <CardHeader><CardTitle className="flex items-center gap-2 text-base"><Server className="w-5 h-5" /> Verbrauch nach App-Teil</CardTitle></CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[520px]">
                  <thead><tr className="text-gray-500 border-b border-gray-100">
                    <th className="py-2 pr-3 text-left font-medium">App-Teil</th>
                    <th className="py-2 px-2 text-right font-medium">Aufrufe</th>
                    <th className="py-2 px-2 text-right font-medium">Tokens</th>
                    <th className="py-2 px-2 text-right font-medium">Kosten</th>
                    <th className="py-2 pl-2 text-right font-medium">Fehler</th>
                  </tr></thead>
                  <tbody>
                    {(sum?.by_source || []).map((r) => (
                      <tr key={r.key} className="border-b border-gray-50" data-testid={`source-${r.key}`}>
                        <td className="py-2 pr-3 text-left font-medium text-gray-800">{r.key}</td>
                        <td className="py-2 px-2 text-right">{nf(r.calls)}</td>
                        <td className="py-2 px-2 text-right text-gray-500">{nf(r.tokens)}</td>
                        <td className="py-2 px-2 text-right text-emerald-700">{usd(r.cost)}</td>
                        <td className="py-2 pl-2 text-right text-orange-600">{nf(r.errors)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Nach Modell + Key-Typ */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card data-testid="by-model-card">
              <CardHeader><CardTitle className="text-base">Nach Modell</CardTitle></CardHeader>
              <CardContent className="space-y-1">
                {(sum?.by_model || []).map((r) => (
                  <div key={r.key} className="flex justify-between text-sm border-b border-gray-50 py-1">
                    <span className="font-medium text-gray-800">{r.key}</span>
                    <span className="text-gray-500">{nf(r.calls)} Aufrufe · {usd(r.cost)}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
            <Card data-testid="by-keytype-card">
              <CardHeader><CardTitle className="flex items-center gap-2 text-base"><KeyRound className="w-5 h-5" /> Nach Key-Typ</CardTitle></CardHeader>
              <CardContent className="space-y-1">
                {(sum?.by_key_type || []).map((r) => (
                  <div key={r.key} className="flex justify-between items-center text-sm border-b border-gray-50 py-1">
                    <KeyBadge type={r.key} />
                    <span className="text-gray-500">{nf(r.calls)} Aufrufe · {usd(r.cost)}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          {/* Konfiguration: welche API je Dienst */}
          <Card data-testid="config-map-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base"><KeyRound className="w-5 h-5" /> Welche API je Dienst (Konfiguration)</CardTitle>
              <p className="text-xs text-gray-500">
                Primär-Provider: <strong>{cfg?.primary_provider}</strong> · Eigene Keys hinterlegt:{' '}
                {['openai', 'anthropic', 'gemini'].map((p) => (
                  <span key={p} className="mr-2">{p}: {cfg?.keys?.[p] ? '✅' : '❌'}</span>
                ))}
              </p>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[560px]">
                  <thead><tr className="text-gray-500 border-b border-gray-100">
                    <th className="py-2 pr-3 text-left font-medium">Dienst</th>
                    <th className="py-2 px-2 text-left font-medium">Provider</th>
                    <th className="py-2 px-2 text-left font-medium">Modell</th>
                    <th className="py-2 px-2 text-center font-medium">Eigener Key?</th>
                    <th className="py-2 pl-2 text-center font-medium">Aktiv</th>
                  </tr></thead>
                  <tbody>
                    {(cfg?.services || []).map((r) => (
                      <tr key={r.service} className="border-b border-gray-50" data-testid={`cfg-${r.service}`}>
                        <td className="py-2 pr-3 text-left font-medium text-gray-800">{r.service}</td>
                        <td className="py-2 px-2 text-left">{r.provider}</td>
                        <td className="py-2 px-2 text-left text-gray-600">{r.model}</td>
                        <td className="py-2 px-2 text-center">{r.key_present ? '✅' : '❌'}</td>
                        <td className="py-2 pl-2 text-center">{r.enabled ? '✅' : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {(cfg?.emergent_fallback_flows || []).length > 0 && (
                <div className="mt-4 rounded-lg bg-amber-50 border border-amber-100 p-3 text-xs text-amber-800">
                  <strong>Hinweis:</strong> Folgende Bereiche können (noch) auf den Emergent-Key zurückfallen und werden auf eure eigene API umgestellt:{' '}
                  {cfg.emergent_fallback_flows.join(', ')}.
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default AIUsagePage;
